import { useState, useEffect } from "react";
import { format, eachDayOfInterval, parseISO } from "date-fns";
import { absenceApi, employeeApi, businessHoursApi, shiftApi } from "../lib/api";
import { getColorById, getCurrencySymbol } from "../lib/utils";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "../components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { CalendarDays, Plus, Pencil, Trash2, CalendarIcon, Palmtree, Stethoscope, Baby, Heart, GraduationCap, HelpCircle, AlertTriangle, Loader2, Users, ArrowRight, Clock } from "lucide-react";
import { toast } from "sonner";
import { cn } from "../lib/utils";

// Absence reason types with icons
const ABSENCE_REASONS = [
  { value: "holiday", label: "Holiday", icon: Palmtree },
  { value: "sick", label: "Sick Leave", icon: Stethoscope },
  { value: "maternity", label: "Maternity Leave", icon: Baby },
  { value: "paternity", label: "Paternity Leave", icon: Baby },
  { value: "bereavement", label: "Bereavement", icon: Heart },
  { value: "training", label: "Training", icon: GraduationCap },
  { value: "custom", label: "Custom", icon: HelpCircle },
];

const getReasonIcon = (reasonType) => {
  const reason = ABSENCE_REASONS.find(r => r.value === reasonType);
  return reason?.icon || HelpCircle;
};

const getReasonLabel = (reasonType) => {
  const reason = ABSENCE_REASONS.find(r => r.value === reasonType);
  return reason?.label || reasonType;
};

const Absences = () => {
  const [absences, setAbsences] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [currency, setCurrency] = useState("GBP");
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAbsence, setEditingAbsence] = useState(null);
  const [form, setForm] = useState({
    employee_id: "",
    start_date: new Date(),
    end_date: new Date(),
    reason_type: "holiday",
    custom_reason: "",
    is_paid: false,
    paid_hours_type: "contracted",
    custom_paid_hours: 8,
    fixed_amount: 118.75,
    fixed_amount_period: "weekly",
  });
  
  // Conflict dialog state
  const [conflictDialogOpen, setConflictDialogOpen] = useState(false);
  const [conflictingShifts, setConflictingShifts] = useState([]);
  const [shiftReassignments, setShiftReassignments] = useState({});
  const [pendingAbsenceData, setPendingAbsenceData] = useState(null);
  const [processingConflicts, setProcessingConflicts] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [absencesRes, employeesRes, businessHoursRes] = await Promise.all([
        absenceApi.getAll(),
        employeeApi.getAll(),
        businessHoursApi.get()
      ]);
      setAbsences(absencesRes.data);
      setEmployees(employeesRes.data);
      setCurrency(businessHoursRes.data?.currency || "GBP");
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load absences");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const openDialog = (absence = null) => {
    if (absence) {
      setEditingAbsence(absence);
      const emp = employees.find(e => e.id === absence.employee_id);
      const empHasContractedHours = emp?.has_contracted_hours && emp?.min_hours > 0;
      setForm({
        employee_id: absence.employee_id,
        start_date: new Date(absence.start_date),
        end_date: new Date(absence.end_date),
        reason_type: absence.reason_type,
        custom_reason: absence.custom_reason || "",
        is_paid: absence.is_paid,
        // If no contracted hours, force custom type (but allow fixed_amount)
        paid_hours_type: absence.paid_hours_type || (empHasContractedHours ? "contracted" : "custom"),
        custom_paid_hours: absence.custom_paid_hours || 8,
        fixed_amount: absence.fixed_amount || 118.75,
        fixed_amount_period: absence.fixed_amount_period || "weekly",
      });
    } else {
      setEditingAbsence(null);
      const firstEmp = employees.length > 0 ? employees[0] : null;
      const firstEmpHasContractedHours = firstEmp?.has_contracted_hours && firstEmp?.min_hours > 0;
      setForm({
        employee_id: firstEmp?.id || "",
        start_date: new Date(),
        end_date: new Date(),
        reason_type: "holiday",
        custom_reason: "",
        is_paid: false,
        // If no contracted hours, default to custom
        paid_hours_type: firstEmpHasContractedHours ? "contracted" : "custom",
        custom_paid_hours: 8,
        fixed_amount: 118.75,
        fixed_amount_period: "weekly",
      });
    }
    setDialogOpen(true);
  };

  // When employee changes, update paid_hours_type if needed
  const handleEmployeeChange = (employeeId) => {
    const emp = employees.find(e => e.id === employeeId);
    const empHasContractedHours = emp?.has_contracted_hours && emp?.min_hours > 0;
    setForm(prev => ({
      ...prev,
      employee_id: employeeId,
      // If new employee doesn't have contracted hours, switch to custom
      paid_hours_type: empHasContractedHours ? prev.paid_hours_type : "custom"
    }));
  };

  const handleSave = async () => {
    if (!form.employee_id) {
      toast.error("Please select an employee");
      return;
    }

    if (form.reason_type === "custom" && !form.custom_reason.trim()) {
      toast.error("Please enter a reason for the absence");
      return;
    }

    // Determine the effective paid_hours_type based on contracted hours
    const emp = employees.find(e => e.id === form.employee_id);
    const empHasContractedHours = emp?.has_contracted_hours && emp?.min_hours > 0;
    
    // If contracted hours type is selected but employee doesn't have contracted hours, switch to custom
    let effectivePaidHoursType = form.paid_hours_type;
    if (form.paid_hours_type === "contracted" && !empHasContractedHours) {
      effectivePaidHoursType = "custom";
    }

    const data = {
      employee_id: form.employee_id,
      start_date: format(form.start_date, "yyyy-MM-dd"),
      end_date: format(form.end_date, "yyyy-MM-dd"),
      reason_type: form.reason_type,
      custom_reason: form.reason_type === "custom" ? form.custom_reason : null,
      is_paid: form.is_paid,
      paid_hours_type: form.is_paid ? effectivePaidHoursType : null,
      // Include custom_paid_hours when type is custom
      custom_paid_hours: form.is_paid && effectivePaidHoursType === "custom" ? form.custom_paid_hours : null,
      // Include fixed amount fields when type is fixed_amount
      fixed_amount: form.is_paid && effectivePaidHoursType === "fixed_amount" ? form.fixed_amount : null,
      fixed_amount_period: form.is_paid && effectivePaidHoursType === "fixed_amount" ? form.fixed_amount_period : null,
    };

    // Only check conflicts for new absences (not edits)
    if (!editingAbsence) {
      try {
        const conflictRes = await shiftApi.checkConflicts({
          employee_id: form.employee_id,
          start_date: data.start_date,
          end_date: data.end_date
        });
        
        if (conflictRes.data.has_conflicts && conflictRes.data.conflicts.length > 0) {
          // Show conflict dialog
          setConflictingShifts(conflictRes.data.conflicts);
          setPendingAbsenceData(data);
          // Initialize reassignments state - default to "delete" for each shift
          const initialReassignments = {};
          conflictRes.data.conflicts.forEach(shift => {
            initialReassignments[shift.id] = { action: "delete", newEmployeeId: "" };
          });
          setShiftReassignments(initialReassignments);
          setConflictDialogOpen(true);
          return;
        }
      } catch (error) {
        console.error("Error checking conflicts:", error);
        // Continue with save even if conflict check fails
      }
    }

    await saveAbsence(data);
  };

  const saveAbsence = async (data) => {
    try {
      if (editingAbsence) {
        await absenceApi.update(editingAbsence.id, data);
        toast.success("Absence updated");
      } else {
        await absenceApi.create(data);
        toast.success("Absence created");
      }
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error("Error saving absence:", error);
      const errorMsg = error.response?.data?.detail || "Failed to save absence";
      toast.error(errorMsg);
    }
  };

  const handleConflictResolution = async () => {
    if (!pendingAbsenceData) return;
    
    setProcessingConflicts(true);
    
    try {
      // Process each conflict based on user's choice
      const shiftsToDelete = [];
      const shiftsToReassign = [];
      
      for (const shift of conflictingShifts) {
        const decision = shiftReassignments[shift.id];
        if (decision.action === "delete") {
          shiftsToDelete.push(shift.id);
        } else if (decision.action === "reassign" && decision.newEmployeeId) {
          shiftsToReassign.push({ shift_id: shift.id, new_employee_id: decision.newEmployeeId });
        }
      }
      
      // Delete shifts first
      if (shiftsToDelete.length > 0) {
        await shiftApi.bulkDelete({ shift_ids: shiftsToDelete });
      }
      
      // Then reassign shifts
      for (const reassignment of shiftsToReassign) {
        await shiftApi.reassign(reassignment);
      }
      
      // Now save the absence
      await saveAbsence(pendingAbsenceData);
      
      setConflictDialogOpen(false);
      setConflictingShifts([]);
      setPendingAbsenceData(null);
      setShiftReassignments({});
      
      const deleteCount = shiftsToDelete.length;
      const reassignCount = shiftsToReassign.length;
      if (deleteCount > 0 || reassignCount > 0) {
        const parts = [];
        if (deleteCount > 0) parts.push(`${deleteCount} shift${deleteCount > 1 ? 's' : ''} deleted`);
        if (reassignCount > 0) parts.push(`${reassignCount} shift${reassignCount > 1 ? 's' : ''} reassigned`);
        toast.info(parts.join(", "));
      }
    } catch (error) {
      console.error("Error processing conflicts:", error);
      toast.error("Failed to process shift conflicts");
    } finally {
      setProcessingConflicts(false);
    }
  };

  const getAvailableEmployeesForReassign = (shiftDate) => {
    // Get employees that can be assigned (excluding the current employee on absence)
    return employees.filter(emp => emp.id !== form.employee_id);
  };

  const handleDelete = async (absenceId) => {
    if (!window.confirm("Are you sure you want to delete this absence?")) return;
    
    try {
      await absenceApi.delete(absenceId);
      toast.success("Absence deleted");
      fetchData();
    } catch (error) {
      console.error("Error deleting absence:", error);
      toast.error("Failed to delete absence");
    }
  };

  const getEmployeeName = (employeeId) => {
    const employee = employees.find(e => e.id === employeeId);
    return employee?.name || "Unknown";
  };

  const getEmployee = (employeeId) => {
    return employees.find(e => e.id === employeeId);
  };

  // Get the currently selected employee for the form
  const selectedEmployee = getEmployee(form.employee_id);
  const hasContractedHours = selectedEmployee?.has_contracted_hours && selectedEmployee?.min_hours > 0;
  // Calculate daily hours from weekly min_hours (assume 5-day week)
  const dailyContractedHours = hasContractedHours ? (selectedEmployee.min_hours / 5).toFixed(1) : 0;

  const calculateDays = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    return diffDays;
  };

  // Sort absences by start_date descending
  const sortedAbsences = [...absences].sort((a, b) => 
    new Date(b.start_date) - new Date(a.start_date)
  );

  return (
    <div className="space-y-6" data-testid="absences-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Absences</h2>
          <p className="text-muted-foreground">
            Manage employee holidays, sick leave, and other absences
          </p>
        </div>
        <Button onClick={() => openDialog()} data-testid="add-absence-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add Absence
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CalendarDays className="w-5 h-5" />
            All Absences
          </CardTitle>
          <CardDescription>
            {absences.length} absence{absences.length !== 1 ? 's' : ''} recorded
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center h-32 text-muted-foreground">
              Loading...
            </div>
          ) : absences.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-32 text-muted-foreground">
              <CalendarDays className="w-8 h-8 mb-2 opacity-50" />
              <p>No absences recorded yet</p>
              <Button variant="link" onClick={() => openDialog()} className="mt-2">
                Add your first absence
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Employee</TableHead>
                  <TableHead>Dates</TableHead>
                  <TableHead>Duration</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead>Paid</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedAbsences.map((absence) => {
                  const employee = getEmployee(absence.employee_id);
                  const color = employee ? getColorById(employee.color) : null;
                  const ReasonIcon = getReasonIcon(absence.reason_type);
                  const days = calculateDays(absence.start_date, absence.end_date);
                  
                  return (
                    <TableRow key={absence.id} data-testid={`absence-row-${absence.id}`}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {employee && (
                            employee.avatar_url ? (
                              <img 
                                src={employee.avatar_url} 
                                alt={employee.name}
                                className="w-8 h-8 rounded-full object-cover border border-border"
                              />
                            ) : (
                              <div className={cn(
                                "w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium",
                                color?.bg,
                                color?.text
                              )}>
                                {employee.name.charAt(0)}
                              </div>
                            )
                          )}
                          <div>
                            <p className="font-medium">{getEmployeeName(absence.employee_id)}</p>
                            {employee && <p className="text-xs text-muted-foreground">{employee.role}</p>}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          <p>{format(new Date(absence.start_date), "MMM d, yyyy")}</p>
                          {absence.start_date !== absence.end_date && (
                            <p className="text-muted-foreground">to {format(new Date(absence.end_date), "MMM d, yyyy")}</p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="font-mono">{days} day{days !== 1 ? 's' : ''}</span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <ReasonIcon className="w-4 h-4 text-muted-foreground" />
                          <span>
                            {absence.reason_type === "custom" 
                              ? absence.custom_reason 
                              : getReasonLabel(absence.reason_type)}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        {absence.is_paid ? (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300">
                            Paid
                            {absence.paid_hours_type === "custom" && ` (${absence.custom_paid_hours}h/day)`}
                            {absence.paid_hours_type === "fixed_amount" && ` (${getCurrencySymbol(currency)}${absence.fixed_amount}/${absence.fixed_amount_period === "weekly" ? "wk" : "day"})`}
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400">
                            Unpaid
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openDialog(absence)}
                            data-testid={`edit-absence-${absence.id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(absence.id)}
                            className="text-destructive hover:text-destructive"
                            data-testid={`delete-absence-${absence.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Absence Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md max-h-[90vh] flex flex-col p-0 gap-0 overflow-hidden">
          <DialogHeader className="flex-shrink-0 px-6 pt-6 pb-4">
            <DialogTitle>
              {editingAbsence ? "Edit Absence" : "Add Absence"}
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto overflow-x-hidden px-6">
            <div className="space-y-4 py-4">
            {/* Employee Selection */}
            <div className="space-y-2">
              <Label>Employee</Label>
              <Select
                value={form.employee_id}
                onValueChange={handleEmployeeChange}
              >
                <SelectTrigger data-testid="absence-employee-select">
                  <SelectValue placeholder="Select employee" />
                </SelectTrigger>
                <SelectContent>
                  {employees.map((emp) => {
                    const color = getColorById(emp.color);
                    return (
                      <SelectItem key={emp.id} value={emp.id}>
                        <div className="flex items-center gap-2">
                          {emp.avatar_url ? (
                            <img 
                              src={emp.avatar_url} 
                              alt={emp.name}
                              className="w-5 h-5 rounded-full object-cover"
                            />
                          ) : (
                            <div className={cn(
                              "w-5 h-5 rounded-full flex items-center justify-center text-xs",
                              color?.bg,
                              color?.text
                            )}>
                              {emp.name.charAt(0)}
                            </div>
                          )}
                          <span>{emp.name}</span>
                          <span className="text-muted-foreground">({emp.role})</span>
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            {/* Date Range */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left font-normal">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {format(form.start_date, "MMM d, yyyy")}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={form.start_date}
                      onSelect={(date) => {
                        if (date) {
                          setForm(prev => ({
                            ...prev,
                            start_date: date,
                            // Ensure end date is not before start date
                            end_date: date > prev.end_date ? date : prev.end_date
                          }));
                        }
                      }}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-2">
                <Label>End Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left font-normal">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {format(form.end_date, "MMM d, yyyy")}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={form.end_date}
                      onSelect={(date) => {
                        if (date) setForm(prev => ({ ...prev, end_date: date }));
                      }}
                      disabled={(date) => date < form.start_date}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            {/* Reason */}
            <div className="space-y-2">
              <Label>Reason</Label>
              <Select
                value={form.reason_type}
                onValueChange={(value) => setForm(prev => ({ ...prev, reason_type: value }))}
              >
                <SelectTrigger data-testid="absence-reason-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ABSENCE_REASONS.map((reason) => {
                    const Icon = reason.icon;
                    return (
                      <SelectItem key={reason.value} value={reason.value}>
                        <div className="flex items-center gap-2">
                          <Icon className="w-4 h-4" />
                          <span>{reason.label}</span>
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            {/* Custom Reason Input */}
            {form.reason_type === "custom" && (
              <div className="space-y-2">
                <Label>Custom Reason</Label>
                <Input
                  value={form.custom_reason}
                  onChange={(e) => setForm(prev => ({ ...prev, custom_reason: e.target.value }))}
                  placeholder="Enter reason for absence"
                  data-testid="absence-custom-reason"
                />
              </div>
            )}

            {/* Paid Leave Toggle */}
            <div className="flex items-center space-x-2">
              <Checkbox
                id="is_paid"
                checked={form.is_paid}
                onCheckedChange={(checked) => setForm(prev => ({ ...prev, is_paid: checked }))}
                data-testid="absence-paid-checkbox"
              />
              <Label htmlFor="is_paid" className="cursor-pointer">
                This is paid leave
              </Label>
            </div>

            {/* Paid Hours Options */}
            {form.is_paid && (
              <div className="space-y-3 pl-6 border-l-2 border-primary/20">
                {/* Pay Type Selection - always show all options */}
                <div className="space-y-2">
                  <Label>Pay calculation method</Label>
                  <Select
                    value={form.paid_hours_type}
                    onValueChange={(value) => setForm(prev => ({ ...prev, paid_hours_type: value }))}
                  >
                    <SelectTrigger data-testid="absence-hours-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {hasContractedHours && (
                        <SelectItem value="contracted">
                          Contracted hours ({dailyContractedHours}h/day)
                        </SelectItem>
                      )}
                      <SelectItem value="custom">
                        Custom hours per day
                      </SelectItem>
                      <SelectItem value="fixed_amount">
                        Fixed amount (e.g., statutory sick pay)
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Contracted hours info */}
                {form.paid_hours_type === "contracted" && hasContractedHours && (
                  <p className="text-xs text-muted-foreground">
                    Will pay {dailyContractedHours} hours per day at their rate ({selectedEmployee.min_hours}h/week ÷ 5 days)
                  </p>
                )}

                {/* Warning if contracted selected but no contracted hours */}
                {form.paid_hours_type === "contracted" && !hasContractedHours && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400">
                      <AlertTriangle className="w-4 h-4" />
                      <span className="text-sm font-medium">No contracted hours set</span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {selectedEmployee?.name || "This employee"} doesn&apos;t have contracted hours configured. Please select a different pay method.
                    </p>
                  </div>
                )}

                {/* Custom hours input */}
                {form.paid_hours_type === "custom" && (
                  <div className="space-y-2">
                    <Label>Hours per day</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.5"
                      value={form.custom_paid_hours}
                      onChange={(e) => setForm(prev => ({ 
                        ...prev, 
                        custom_paid_hours: parseFloat(e.target.value) || 0 
                      }))}
                      className="w-32"
                      data-testid="absence-custom-hours"
                    />
                    <p className="text-xs text-muted-foreground">
                      Used for cost calculation (hours × employee&apos;s hourly rate)
                    </p>
                  </div>
                )}

                {/* Fixed amount input */}
                {form.paid_hours_type === "fixed_amount" && (
                  <div className="space-y-3">
                    <div className="space-y-2">
                      <Label>Amount</Label>
                      <div className="flex items-center gap-2">
                        <span className="text-muted-foreground">{getCurrencySymbol(currency)}</span>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={form.fixed_amount}
                          onChange={(e) => setForm(prev => ({ 
                            ...prev, 
                            fixed_amount: parseFloat(e.target.value) || 0 
                          }))}
                          className="w-32"
                          data-testid="absence-fixed-amount"
                        />
                        <Select
                          value={form.fixed_amount_period}
                          onValueChange={(value) => setForm(prev => ({ ...prev, fixed_amount_period: value }))}
                        >
                          <SelectTrigger className="w-28" data-testid="absence-fixed-period-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="daily">per day</SelectItem>
                            <SelectItem value="weekly">per week</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    {form.fixed_amount_period === "weekly" && form.fixed_amount > 0 && (
                      <p className="text-xs text-muted-foreground">
                        = {getCurrencySymbol(currency)}{(form.fixed_amount / 7).toFixed(2)} per day (weekly amount ÷ 7)
                      </p>
                    )}
                    <p className="text-xs text-muted-foreground">
                      Use this for statutory sick pay or other fixed-rate pay. UK SSP is £118.75/week for 2025.
                    </p>
                  </div>
                )}
              </div>
            )}
            </div>
          </div>

          <DialogFooter className="flex-shrink-0 px-6 pb-6 pt-4 border-t">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} data-testid="absence-save-btn">
              {editingAbsence ? "Update" : "Add"} Absence
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Shift Conflict Resolution Dialog */}
      <AlertDialog open={conflictDialogOpen} onOpenChange={setConflictDialogOpen}>
        <AlertDialogContent className="max-w-2xl max-h-[90vh] flex flex-col p-0 gap-0 overflow-hidden">
          <AlertDialogHeader className="flex-shrink-0 px-6 pt-6 pb-4 border-b">
            <AlertDialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="w-5 h-5" />
              Shift Conflicts Detected
            </AlertDialogTitle>
            <AlertDialogDescription>
              {getEmployeeName(form.employee_id)} has {conflictingShifts.length} shift{conflictingShifts.length > 1 ? 's' : ''} scheduled during this absence period. 
              Choose how to handle each conflict.
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          <div className="flex-1 overflow-y-auto px-6 py-4">
            <div className="space-y-4">
              {conflictingShifts.map((shift) => {
                const decision = shiftReassignments[shift.id] || { action: "delete", newEmployeeId: "" };
                const availableEmployees = getAvailableEmployeesForReassign(shift.date);
                
                return (
                  <div key={shift.id} className="border rounded-lg p-4 space-y-3 bg-muted/30" data-testid={`conflict-shift-${shift.id}`}>
                    {/* Shift Details */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <CalendarDays className="w-4 h-4 text-muted-foreground" />
                        <div>
                          <p className="font-medium">{format(new Date(shift.date), "EEEE, MMM d, yyyy")}</p>
                          <p className="text-sm text-muted-foreground flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {shift.start_time} - {shift.end_time}
                            {shift.label && <span className="ml-2">({shift.label})</span>}
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Action Selection */}
                    <div className="flex flex-col sm:flex-row gap-3 pt-2">
                      <Button
                        type="button"
                        variant={decision.action === "delete" ? "default" : "outline"}
                        size="sm"
                        className={cn(
                          "flex-1",
                          decision.action === "delete" && "bg-red-600 hover:bg-red-700"
                        )}
                        onClick={() => setShiftReassignments(prev => ({
                          ...prev,
                          [shift.id]: { action: "delete", newEmployeeId: "" }
                        }))}
                        data-testid={`delete-shift-${shift.id}`}
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete Shift
                      </Button>
                      <Button
                        type="button"
                        variant={decision.action === "reassign" ? "default" : "outline"}
                        size="sm"
                        className="flex-1"
                        onClick={() => setShiftReassignments(prev => ({
                          ...prev,
                          [shift.id]: { action: "reassign", newEmployeeId: prev[shift.id]?.newEmployeeId || "" }
                        }))}
                        data-testid={`reassign-shift-${shift.id}`}
                      >
                        <Users className="w-4 h-4 mr-2" />
                        Reassign
                      </Button>
                    </div>
                    
                    {/* Reassign Employee Selection */}
                    {decision.action === "reassign" && (
                      <div className="pt-2 border-t">
                        <Label className="text-sm mb-2 block">Reassign to:</Label>
                        <Select
                          value={decision.newEmployeeId}
                          onValueChange={(value) => setShiftReassignments(prev => ({
                            ...prev,
                            [shift.id]: { ...prev[shift.id], newEmployeeId: value }
                          }))}
                        >
                          <SelectTrigger data-testid={`reassign-employee-select-${shift.id}`}>
                            <SelectValue placeholder="Select employee" />
                          </SelectTrigger>
                          <SelectContent>
                            {availableEmployees.map((emp) => {
                              const color = getColorById(emp.color);
                              return (
                                <SelectItem key={emp.id} value={emp.id}>
                                  <div className="flex items-center gap-2">
                                    {emp.avatar_url ? (
                                      <img 
                                        src={emp.avatar_url} 
                                        alt={emp.name}
                                        className="w-5 h-5 rounded-full object-cover"
                                      />
                                    ) : (
                                      <div className={cn(
                                        "w-5 h-5 rounded-full flex items-center justify-center text-xs",
                                        color?.bg,
                                        color?.text
                                      )}>
                                        {emp.name.charAt(0)}
                                      </div>
                                    )}
                                    <span>{emp.name}</span>
                                    <span className="text-muted-foreground">({emp.role})</span>
                                  </div>
                                </SelectItem>
                              );
                            })}
                          </SelectContent>
                        </Select>
                        {decision.action === "reassign" && !decision.newEmployeeId && (
                          <p className="text-xs text-amber-600 mt-1">Please select an employee to reassign this shift</p>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          
          <AlertDialogFooter className="flex-shrink-0 px-6 py-4 border-t">
            <AlertDialogCancel 
              onClick={() => {
                setConflictDialogOpen(false);
                setConflictingShifts([]);
                setPendingAbsenceData(null);
                setShiftReassignments({});
              }}
              disabled={processingConflicts}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConflictResolution}
              disabled={
                processingConflicts ||
                Object.values(shiftReassignments).some(
                  decision => decision.action === "reassign" && !decision.newEmployeeId
                )
              }
              data-testid="confirm-conflict-resolution"
            >
              {processingConflicts ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <ArrowRight className="w-4 h-4 mr-2" />
                  Confirm & Add Absence
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Absences;
